import { useEffect, useState } from "react";
import { PUBLIC_SETTINGS_FALLBACK } from "../config/site";
import { groupDigitsTrim } from "./format";

export type PublicSettings = {
  premiumPrice: string;
  freeTierMonths: number;
  supportTelegramLink: string;
  supportPhone: string;
};

type PublicSettingsResponse = {
  premium_price?: unknown;
  free_tier_months?: unknown;
  support_telegram_link?: unknown;
  support_phone?: unknown;
};

const FALLBACK: PublicSettings = { ...PUBLIC_SETTINGS_FALLBACK };
const API_BASE = (
  (import.meta.env.VITE_API_URL as string | undefined) ?? "https://api.nasiyah.uz"
).replace(/\/$/, "");

let cached: PublicSettings | null = null;
let pending: Promise<PublicSettings> | null = null;

function isHttpUrl(value: unknown): value is string {
  if (typeof value !== "string") return false;
  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
}

function parseResponse(data: PublicSettingsResponse): PublicSettings {
  const price = typeof data.premium_price === "string" ? Number(data.premium_price) : NaN;
  const months = data.free_tier_months;
  return {
    premiumPrice:
      Number.isFinite(price) && price >= 0
        ? (data.premium_price as string)
        : FALLBACK.premiumPrice,
    freeTierMonths:
      Number.isInteger(months) && (months as number) >= 1 && (months as number) <= 120
        ? (months as number)
        : FALLBACK.freeTierMonths,
    supportTelegramLink: isHttpUrl(data.support_telegram_link)
      ? data.support_telegram_link
      : FALLBACK.supportTelegramLink,
    supportPhone:
      typeof data.support_phone === "string" && /^\+[1-9]\d{7,14}$/.test(data.support_phone)
        ? data.support_phone
        : FALLBACK.supportPhone,
  };
}

function loadPublicSettings(): Promise<PublicSettings> {
  if (cached) return Promise.resolve(cached);
  if (pending) return pending;

  pending = fetch(`${API_BASE}/api/settings/settings/get_setting/`)
    .then((response) => {
      if (!response.ok) throw new Error("public settings request failed");
      return response.json() as Promise<PublicSettingsResponse>;
    })
    .then(parseResponse)
    .catch(() => FALLBACK)
    .then((settings) => {
      cached = settings;
      return settings;
    });
  return pending;
}

export function usePublicSettings(): PublicSettings {
  const [settings, setSettings] = useState<PublicSettings>(cached ?? FALLBACK);

  useEffect(() => {
    let active = true;
    void loadPublicSettings().then((next) => {
      if (active) setSettings(next);
    });
    return () => {
      active = false;
    };
  }, []);

  return settings;
}

/** "165000.00" → "165 000" (up to 2 decimals, uz/ru grouping). Not Intl — see format.ts. */
export function formatPremiumPrice(value: string): string {
  return groupDigitsTrim(Number(value), 2);
}

export function formatSupportPhone(value: string): string {
  const match = value.match(/^\+998(\d{2})(\d{3})(\d{2})(\d{2})$/);
  return match ? `+998 ${match[1]} ${match[2]} ${match[3]} ${match[4]}` : value;
}
